// Type-safe utility functions for rollout status
// Location: src/shared/rollout-dialog/store/rollout.utils.ts

import { formDayInBerlin } from '@shared/berlin-time';
import { isAfter } from 'date-fns';
import { RolloutStatus } from './rollout.const';

/**
 * A rollout range may collapse to a single day: the dialog adjusts the opposite date to the
 * changed one when the range inverts, and a single day can be rolled out on its own. Compared
 * at day level because the two dates carry the time of day they were created with.
 */
export const isValidRolloutRange = (startDate: Date, endDate: Date): boolean =>
  !isAfter(formDayInBerlin(startDate), formDayInBerlin(endDate));

/**
 * Type-safe status comparison utilities
 * Use these instead of direct string comparisons to prevent case sensitivity bugs
 */

export const isRolloutStatus = {
  success: (status: RolloutStatus): boolean => status === RolloutStatus.SUCCESS,
  warning: (status: RolloutStatus): boolean => status === RolloutStatus.WARNING,
  error: (status: RolloutStatus): boolean => status === RolloutStatus.ERROR,
  idle: (status: RolloutStatus): boolean => status === RolloutStatus.IDLE,
  loading: (status: RolloutStatus): boolean => status === RolloutStatus.LOADING,
} as const;

/**
 * Check if status indicates completion (success, warning, or error)
 */
export const isCompleted = (status: RolloutStatus): boolean => {
  return (
    status === RolloutStatus.SUCCESS ||
    status === RolloutStatus.WARNING ||
    status === RolloutStatus.ERROR
  );
};

/**
 * Check if status has any failures (warning or error)
 */
export const hasFailures = (status: RolloutStatus): boolean => {
  return status === RolloutStatus.WARNING || status === RolloutStatus.ERROR;
};

/**
 * Get notification color for status
 */
export const getNotificationColor = (status: RolloutStatus): 'success' | 'warning' | 'error' => {
  switch (status) {
    case RolloutStatus.SUCCESS:
      return 'success';
    case RolloutStatus.WARNING:
      return 'warning';
    case RolloutStatus.ERROR:
      return 'error';
    default:
      return 'success'; // Default fallback
  }
};

// Usage examples:
// ✅ isRolloutStatus.warning(branch.status)
// ✅ hasFailures(rolloutStatus)
// ✅ getNotificationColor(status)
